import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import ReactModal from 'react-modal'

import { withTranslation, i18n, Router } from '../i18n'

import Layout from '../components/Layout'
import FilterMenu from '../components/FilterMenu'
import SearchPanel from '../components/SearchPanel'
import GenresPanel from '../components/GenresPanel'
import Grid from '../components/Grid'
import Footer from '../components/Footer'
import Button from '../components/Button'

import { Container, GlobalStyle } from '../styles/listsStyles'
import { artistsFilters } from '../constants/filters'
import { favoriteRoute } from '../api/routes'
import { generateQueryString } from '../api/utils'

@inject('store')
@observer
class Artists extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    currentLanguage: PropTypes.string.isRequired,
    store: PropTypes.instanceOf(Object).isRequired,
    genres: PropTypes.arrayOf(String).isRequired,
    datetime: PropTypes.string.isRequired,
    ordering: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }

  state = {
    genres: this.props.genres,
    datetime: this.props.datetime,
    ordering: this.props.ordering,
    search: this.props.search,
    performanceIdToDelete: undefined,
    isOpenClosingModal: false
  }

  static getInitialProps({ req, query }) {
    const currentLanguage = req ? req.language : i18n.language
    return {
      namespacesRequired: ['common', 'program', 'genres', 'contacts', 'auth', 'reset-password'],
      currentLanguage,
      datetime: query.datetime || '',
      ordering: query.ordering || '-favorited_count',
      search: query.search || '',
      genres: query.genres ? query.genres.split(',') : []
    }
  }

  applyFilters = (filters) => {
    Router.replace(`/artists?${generateQueryString({ ...Router.query, ...filters })}`)
    this.setState({ ...filters })
  }

  handleMainFilterChange = (filter) => {
    const { datetime } = this.state
    if (!filter.startsWith('datetime')) {
      if ((filter === 'artist__name' || filter === '-favorited_count') && datetime !== '') {
        this.applyFilters({ ordering: filter, datetime: '' })
      } else this.applyFilters({ ordering: filter })
    } else {
      const isoTime = filter.split('=')[1]
      if (datetime && datetime === isoTime) {
        this.applyFilters({ datetime: '' })
        return
      }
      this.applyFilters({ datetime: isoTime })
    }
  }

  handleGenreChange = (genre) => {
    const { genres } = this.state
    if (genres.includes(genre)) {
      genres.splice(genres.indexOf(genre), 1)
    } else {
      genres.push(genre)
    }

    if (genres.length === 0) {
      this.clearGenres()
      return
    }
    this.applyFilters({ genres: [...genres] })
  }

  handleSearchChange = (text) => {
    this.applyFilters({ search: text })
  }

  clearGenres = () => {
    this.applyFilters({ genres: [] })
  }

  onStarPress = (id, favorited) => {
    const {
      t,
      store: {
        toggleLoginModal,
        user: { token }
      }
    } = this.props

    if (!token) {
      toggleLoginModal(true, t('make_an_account'))
      return
    }

    if (favorited) {
      this.setState({ isOpenClosingModal: true, performanceIdToDelete: id })
    } else {
      favoriteRoute(id, 'performances', undefined, token, favorited).then(() => {
        this.gridRef.setStar(id, !favorited)
      })
    }
  }

  render() {
    const {
      t,
      currentLanguage,
      store: {
        user: { token }
      }
    } = this.props
    const {
      genres, datetime, search, ordering, isOpenClosingModal, performanceIdToDelete
    } = this.state

    const filters = {
      artist__genres: [...genres],
      search,
      ordering,
      datetime
    }

    return (
      <Layout meta={{ title: t('PAGE_ARTISTS_TITLE') }} coverImageUrl="artists-cover" currentLanguage={currentLanguage}>
        <GlobalStyle />
        <ReactModal
          ariaHideApp={false}
          isOpen={isOpenClosingModal}
          onRequestClose={() => {
            this.setState({
              isOpenClosingModal: false,
              performanceIdToDelete: undefined
            })
          }}
          overlayClassName="ClosingModal_Overlay"
          className="ClosingModal_Content"
        >
          <div className="closeWrapper">
            <h3>{t('program:delete_artist')}</h3>
            <p className="sureDelete">{t('program:sure_delete_artist')}</p>
            <div className="modalButtons">
              <Button
                onPress={() => {
                  favoriteRoute(performanceIdToDelete, 'performances', undefined, token, true)
                    .then(() => {
                      this.gridRef.setStar(performanceIdToDelete, false)
                      this.setState({
                        performanceIdToDelete: undefined,
                        isOpenClosingModal: false
                      })
                    })
                    .catch(err => console.warn({ err }))
                }}
              >
                {t('common:delete')}
              </Button>
              <Button
                light
                onPress={() => {
                  this.setState({ isOpenClosingModal: false })
                }}
              >
                {t('common:cancel')}
              </Button>
            </div>
          </div>
        </ReactModal>
        <Container>
          <div className="content">
            <div className="searchPanel spArtists">
              <SearchPanel
                placeholder={t('find_artist')}
                buttonText={t('search')}
                value={search}
                onChange={this.handleSearchChange}
              />
            </div>
            <div className="row">
              <div className="menu spMenu">
                <FilterMenu
                  items={artistsFilters}
                  value={ordering || artistsFilters[0].value}
                  onChange={this.handleMainFilterChange}
                  hasTimes={ordering === artistsFilters[2].value}
                  datetime={datetime}
                />
              </div>
              <div className="searchPanelMobile">
                <SearchPanel
                  placeholder={t('find_artist')}
                  buttonText={t('search')}
                  value={search}
                  onChange={this.handleSearchChange}
                />
              </div>
              <div className="list">
                <div className="filters">
                  <GenresPanel values={genres} onChange={this.handleGenreChange} clear={this.clearGenres} headliner />
                </div>
                <div className="gridContainer">
                  <Grid
                    ref={ref => (this.gridRef = ref)}
                    filters={filters}
                    entity="artists"
                    showStar
                    onStarPress={this.onStarPress}
                    token={token}
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
        <Footer />
      </Layout>
    )
  }
}

export default withTranslation(['common', 'program'])(Artists)
